import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, RotateCcw, ShieldAlert, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtDateTime } from "@/lib/intl";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { applyOverrides, check, EDITABLE_TOKENS } from "@/lib/theme/colors";
import PresetCard from "@/components/appearance/PresetCard";
import ColorField from "@/components/appearance/ColorField";
import ContrastIssues, { mergeFixes } from "@/components/appearance/ContrastIssues";
import ConfirmAction from "@/components/appearance/ConfirmAction";
import ClinicHistory from "@/components/appearance/ClinicHistory";
import { PolicySwitches, PresetApprovals } from "@/components/appearance/ClinicControls";

const fromClinic = (c) => ({ default_preset_id: c.default_preset_id, overrides: c.overrides || {}, policy: { ...c.policy } });

function Block({ title, hint, children }) {
  return (
    <section className="space-y-2.5">
      <div>
        <h4 className="text-sm font-semibold text-le-text">{title}</h4>
        {hint && <p className="mt-0.5 text-xs text-le-t-2">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? { t: d } : { t: d?.message || "Something went wrong", d: d?.issues?.map((i) => i.message).join(" ") };
};

export default function ClinicAppearance() {
  const th = useTheme();
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showColours, setShowColours] = useState(false);

  const accept = (data, keepForm) => { setState(data); if (!keepForm) setForm(fromClinic(data.clinic)); };
  useEffect(() => {
    // Resume an unpublished clinic draft that is still being previewed.
    const resume = th.draft?.kind === "clinic" ? th.draft.form : null;
    api.get("/admin/appearance")
      .then((r) => { setState(r.data); setForm(resume || fromClinic(r.data.clinic)); })
      .catch(() => toast.error("Couldn't load the clinic appearance"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const byId = useMemo(() => Object.fromEntries((state?.presets || []).map((p) => [p.id, p])), [state]);
  const base = form ? byId[form.default_preset_id]?.tokens : null;
  const issues = useMemo(() => (base ? check(applyOverrides(base, form.overrides)) : []), [base, form]);
  const previewing = th.draft?.kind === "clinic";

  useEffect(() => {
    if (previewing && base) th.previewClinic(form, base, byId[form.default_preset_id]?.name);
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state || !form) return <Card className="p-6 text-sm text-le-t-2">Loading clinic appearance…</Card>;
  const changed = JSON.stringify(form) !== JSON.stringify(fromClinic(state.clinic));
  const flagged = new Set(issues.flatMap((i) => i.tokens));
  const live = byId[state.clinic.default_preset_id];

  async function run(fn, ok, keepForm) {
    setBusy(true);
    try {
      const r = await fn();
      accept(r.data, keepForm);
      await th.reload();
      toast.success(ok);
      return true;
    } catch (e) {
      const m = errMsg(e);
      toast.error(m.t, { description: m.d });
      return false;
    } finally {
      setBusy(false);
    }
  }
  const stopPreview = () => { if (previewing) th.cancelPreview(); };
  const doConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    if (c.kind === "publish") { if (await run(() => api.put("/admin/appearance", form), "Clinic default published")) stopPreview(); }
    if (c.kind === "reset") { if (await run(() => api.post("/admin/appearance/reset"), "Clinic theme reset to Clinical Modern")) stopPreview(); }
    if (c.kind === "rollback") { if (await run(() => api.post("/admin/appearance/rollback", { audit_id: c.entry.id }), "Earlier clinic theme restored")) stopPreview(); }
  };
  const setOverride = (k, v) => setForm((f) => ({ ...f, overrides: { ...f.overrides, [k]: v } }));

  return (
    <Card className="space-y-8 rounded-xl border border-le-border p-6 shadow-sm" data-testid="clinic-appearance">
      <div className="space-y-1">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Owner only</div>
        <h3 className="font-display text-2xl font-bold text-le-text">Clinic appearance</h3>
        <p className="max-w-2xl text-sm text-le-t-2">
          Sets the default look for the {state.followers} {state.followers === 1 ? "person" : "people"} who follow the clinic default. {state.personal} with their own theme keep it. Theme changes never touch clinical records, bookings or payments.
        </p>
        <p className="text-xs text-le-t-2" data-testid="clinic-published-line">
          Live now: <span className="font-medium text-le-text">{live?.name}</span> · version {state.clinic.version}
          {state.clinic.updated_at && <> · {fmtDateTime(state.clinic.updated_at)}</>}
        </p>
      </div>

      <Block title="Clinic default theme">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {state.presets.filter((p) => p.enabled).map((p) => (
            <PresetCard key={p.id} preset={p} selected={form.default_preset_id === p.id} testId={`clinic-preset-${p.id}`}
              onSelect={(id) => setForm((f) => ({ ...f, default_preset_id: id }))} />
          ))}
        </div>
      </Block>

      <Block title="Clinic colour adjustments (optional)" hint="Fine-tune the chosen theme for everyone. Unreadable combinations can't be published.">
        <Button variant="outline" size="sm" onClick={() => setShowColours((v) => !v)} data-testid="clinic-colours-toggle">
          {showColours ? "Hide colour adjustments" : `Adjust colours${Object.keys(form.overrides).length ? ` (${Object.keys(form.overrides).length} changed)` : ""}`}
        </Button>
        {showColours && (
          <div className="space-y-3">
            <div className="divide-y divide-zinc-100 rounded-lg border border-le-border bg-le-surface px-3">
              {EDITABLE_TOKENS.map((k) => (
                <ColorField key={k} token={k} testPrefix="clinic-color" value={form.overrides[k] || base[k]} flagged={flagged.has(k)} onChange={(v) => setOverride(k, v)} />
              ))}
            </div>
            <ContrastIssues issues={issues} testPrefix="clinic-contrast" onFix={() => setForm((f) => ({ ...f, overrides: { ...f.overrides, ...mergeFixes(issues) } }))} />
            {Object.keys(form.overrides).length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, overrides: {} }))} data-testid="clinic-colours-clear">
                <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Remove colour adjustments
              </Button>
            )}
          </div>
        )}
      </Block>

      <Block title="What staff can change">
        <PolicySwitches policy={form.policy} onChange={(policy) => setForm((f) => ({ ...f, policy }))} />
      </Block>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-le-border bg-le-subtle p-3" data-testid="clinic-actions">
        {previewing ? (
          <Button variant="outline" onClick={th.cancelPreview} data-testid="clinic-preview-stop"><EyeOff className="h-4 w-4" aria-hidden /> Stop preview</Button>
        ) : (
          <Button variant="outline" onClick={() => { th.previewClinic(form, base, byId[form.default_preset_id]?.name); toast.info("Previewing on your screen only", { description: "No one else sees this until you publish." }); }} disabled={issues.length > 0} data-testid="clinic-preview-btn">
            <Eye className="h-4 w-4" aria-hidden /> Preview on my screen
          </Button>
        )}
        <Button variant="ghost" onClick={() => { setForm(fromClinic(state.clinic)); stopPreview(); }} disabled={!changed} data-testid="clinic-discard-btn">
          Discard changes
        </Button>
        <Button className="ml-auto" onClick={() => setConfirm({ kind: "publish" })} disabled={!changed || issues.length > 0 || busy} data-testid="clinic-publish-btn">
          <Upload className="h-4 w-4" aria-hidden /> Publish clinic default
        </Button>
      </div>

      <Block title="Approved themes" hint="Choose which themes staff can pick. Clinical Modern and High Contrast always stay available.">
        <PresetApprovals presets={state.presets} locked={state.locked_presets} current={state.clinic.default_preset_id} busy={busy}
          onToggle={(id, enabled) => run(() => api.put(`/admin/appearance/presets/${id}`, { enabled }), enabled ? "Theme turned on" : "Theme hidden from staff", true)} />
      </Block>

      <Block title="History" hint="Every published change, who made it and when. Restore puts an earlier version live again.">
        <ClinicHistory history={state.history} presetsById={byId} currentVersion={state.clinic.version} busy={busy}
          onRestore={(entry) => setConfirm({ kind: "rollback", entry })} />
      </Block>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
        <p className="flex items-start gap-2 text-sm text-red-900">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          If the clinic theme ever looks wrong, reset it to the safe built-in Clinical Modern theme. Staff choices and clinical data are untouched.
        </p>
        <Button variant="outline" className="border-red-300 bg-white text-red-800 hover:bg-red-100" onClick={() => setConfirm({ kind: "reset" })} disabled={busy} data-testid="clinic-reset-safe-btn">
          Reset to safe theme
        </Button>
      </div>

      <ConfirmAction
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        testId="clinic-confirm"
        title={confirm?.kind === "publish" ? "Publish the new clinic default?" : confirm?.kind === "reset" ? "Reset the clinic theme?" : "Restore this earlier version?"}
        description={confirm?.kind === "reset"
          ? `The clinic default goes back to Clinical Modern with no colour adjustments for the ${state.followers} people following it.`
          : confirm?.kind === "rollback"
            ? `This puts that version's theme, colour adjustments and staff choice settings live again for the ${state.followers} people who follow the clinic default.`
            : `This changes the look for the ${state.followers} people who follow the clinic default. People with their own theme keep it.`}
        confirmLabel={confirm?.kind === "publish" ? "Publish" : confirm?.kind === "reset" ? "Reset theme" : "Restore"}
        danger={confirm?.kind === "reset"}
        onConfirm={doConfirm}
      />
    </Card>
  );
}
